import { AssetType } from '/@/modules/asset/enums/AssetType';
import EnumRelated from '/@/modules/core/app/helpers/EnumRelated';
import { getters } from '/@/modules/core/app/helpers/store';
import Lang from '/@/modules/core/app/services/Lang';
import { DocumentMetaType } from '/@/modules/document/constants/DocumentMetaType';
import { DocumentType } from '/@/modules/document/enums/DocumentType';
import { FuneralAndEstateExpenseType } from '/@/modules/liability/enums/FuneralAndEstateExpenseType';
import { LiabilityType } from '/@/modules/liability/enums/LiabilityType';
import { PersonalExpenseType } from '/@/modules/liability/enums/PersonalExpenseType';
import { PredefinedJurisdiction } from '/@/modules/predefined/enums/PredefinedJurisdiction';

export class DocumentSubType extends EnumRelated {
  /**
   * @type {string}
   */
  key;

  /**
   * @type {string}
   */
  value;

  static IDENTIFICATION_BIRTH_CERTIFICATE = {
    key: 'identification_birth_certificate',
    get value() {
      return Lang.t('document.birth-certificate');
    }
  };

  static IDENTIFICATION_DRIVERS_LICENSE = {
    key: 'identification_drivers_license',
    get value() {
      return Lang.t('document.drivers-license');
    },
    metaType: DocumentMetaType.DOCUMENT_DRIVERS_LICENSE_META
  };

  static IDENTIFICATION_HEALTH_INSURANCE_CARD = {
    key: 'identification_health_insurance_card',
    get value() {
      return Lang.t('document.health-insurance-card');
    }
  };

  static IDENTIFICATION_PASSPORT = {
    key: 'identification_passport',
    get value() {
      return Lang.t('document.passport');
    },
    metaType: DocumentMetaType.DOCUMENT_PASSPORT_META
  };

  static IDENTIFICATION_OTHERS = {
    key: 'identification_others',
    get value() {
      return Lang.t('document.other-id-valid');
    },
    metaType: DocumentMetaType.DOCUMENT_OTHER_META
  };

  static ASSET_APPRAISAL = {
    key: 'asset_appraisal',
    get value() {
      return Lang.t('asset.appraisal');
    }
  };

  static ASSET_BANK_STATEMENTS = {
    key: 'asset_bank_statements',
    get value() {
      return Lang.t('document.bank-statements');
    }
  };

  static ASSET_FIREARM_LICENSE = {
    key: 'asset_firearm_license',
    get value() {
      return Lang.t('asset.firearm-license');
    }
  };

  static ASSET_OTHERS = {
    key: 'asset_others',
    get value() {
      return Lang.t('asset.other');
    },
    metaType: DocumentMetaType.DOCUMENT_OTHER_META
  };

  static ASSET_POLICY_CONTRACT = {
    key: 'asset_policy_contract',
    get value() {
      return Lang.t('document.policy-contract');
    }
  };

  static ASSET_PRE_ARRANGEMENT_CONTRACT = {
    key: 'asset_pre_arrangement_contract',
    get value() {
      return Lang.t('document.pre-contract');
    }
  };

  static ASSET_PRE_ARRANGEMENT_CEMETERY_PROPERTY_DEED = {
    key: 'asset_pre_arrangement_cemetery_property_deed',
    get value() {
      return Lang.t('document.deed');
    }
  };

  static ASSET_PROPERTY_TITLE = {
    key: 'asset_property_title',
    get value() {
      return Lang.t('document.property-title');
    }
  };

  static ASSET_LAND_DEED = {
    key: 'asset_land_deed',
    get value() {
      return Lang.t('document.asset-property-deed');
    }
  };

  static ASSET_LAND_PROOF_INCOME = {
    key: 'asset_land_proof_income',
    get value() {
      return Lang.t('document.asset-land-proof-income');
    }
  };

  static ASSET_STOCK_OPTIONS = {
    key: 'asset_stock_options',
    get value() {
      return Lang.t('document.stock-option-agreement');
    }
  };

  static ASSET_TAX_REPORT = {
    key: 'asset_tax_report',
    get value() {
      return Lang.t('document.tax-report');
    }
  };

  static BANKING_BANK_STATEMENT = {
    key: 'banking_bank_statement',
    get value() {
      return Lang.t('document.bank-statement');
    }
  };

  static BANKING_CHEQUE = {
    key: 'banking_cheque',
    get value() {
      return Lang.t('document.cheque-specimen');
    }
  };

  static BANKING_FINANCIAL_INSTITUTION_LETTER = {
    key: 'banking_financial_institution_letter',
    get value() {
      return Lang.t('document.financial-institution-letter');
    }
  };

  static BANKING_OTHERS = {
    key: 'banking_others',
    get value() {
      return Lang.t('document.other');
    },
    metaType: DocumentMetaType.DOCUMENT_OTHER_META
  };

  static LIABILITY_APPRAISAL = {
    key: 'liability_appraisal',
    get value() {
      return Lang.t('asset.appraisal');
    }
  };

  static LIABILITY_BANK_STATEMENTS = {
    key: 'liability_bank_statements',
    get value() {
      return Lang.t('document.bank-statements');
    }
  };

  static LIABILITY_BILL = {
    key: 'liability_bill',
    get value() {
      return Lang.t('document.bill');
    }
  };

  static LIABILITY_FUNERAL_CONTRACT = {
    key: 'liability_funeral_contract',
    get value() {
      return Lang.t('document.funeral-contract');
    }
  };

  static LIABILITY_FUNERAL_INVOICE = {
    key: 'liability_funeral_invoice',
    get value() {
      return Lang.t('document.funeral-invoice');
    }
  };

  static LIABILITY_LEASE = {
    key: 'liability_lease',
    get value() {
      return Lang.t('document.lease');
    }
  };

  static LIABILITY_OTHERS = {
    key: 'liability_others',
    get value() {
      return Lang.t('asset.other');
    },
    metaType: DocumentMetaType.DOCUMENT_OTHER_META
  };

  static LIABILITY_POTENTIAL_SPOUSE_CLAIM = {
    key: 'liability_potential_spouse_claim',
    get value() {
      return Lang.t('liability.potential-spouse-claim');
    }
  };

  static LIABILITY_PROBATE_REFEREE_INVOICE = {
    key: 'liability_probate_referee_invoice',
    get value() {
      return Lang.t('document.probate-referee-invoice');
    }
  };

  static LIABILITY_TAX = {
    key: 'liability_tax',
    get value() {
      return Lang.t('document.tax');
    }
  };

  static LIABILITY_EXECUTOR_DISBURSEMENT_INVOICE = {
    key: 'liability_executor_disbursement_invoice',
    get value() {
      return Lang.t('document.executor-disbursement-invoice');
    }
  };

  static ESTATE_ADMINISTRATION_LETTERS = {
    key: 'estate_administration_letters',
    get value() {
      return Lang.t('document.letters-administration-of');
    }
  };

  static ESTATE_PROOF_OF_WILL_SIGNED = {
    key: 'estate_proof_of_will_signed',
    get value() {
      return Lang.t('document.proof-will-of');
    }
  };

  static ESTATE_AFFIDAVIT_OF_EXECUTION = {
    key: 'estate_affidavit_of_execution',
    get value() {
      return Lang.t('document.affidavit-of-execution');
    }
  };

  static ESTATE_AFFIDAVIT_OF_EXECUTION_SIGNED = {
    key: 'estate_affidavit_of_execution_signed',
    get value() {
      return Lang.t('document.affidavit-of-signed');
    }
  };

  static ESTATE_REPRESENTATION_AGREEMENT = {
    key: 'estate_representation_agreement',
    get value() {
      return Lang.t('document.representation-agreement');
    }
  };

  static ESTATE_REPRESENTATION_AGREEMENT_SIGNED = {
    key: 'estate_representation_agreement_signed',
    get value() {
      return Lang.t('document.representation-agreement-signed');
    }
  };

  static ESTATE_ENDURING_POWER_OF_ATTORNEY = {
    key: 'estate_enduring_power_of_attorney',
    get value() {
      return Lang.t('document.enduring-power-of-attorney');
    }
  };

  static ESTATE_ENDURING_POWER_OF_ATTORNEY_SIGNED = {
    key: 'estate_enduring_power_of_attorney_signed',
    get value() {
      return Lang.t('document.enduring-power-of-attorney-signed');
    }
  };

  static ESTATE_PERSONAL_DIRECTIVE = {
    key: 'estate_personal_directive',
    get value() {
      return Lang.t('document.personal-directive');
    }
  };

  static ESTATE_PERSONAL_DIRECTIVE_SIGNED = {
    key: 'estate_personal_directive_signed',
    get value() {
      return Lang.t('document.personal-directive-signed');
    }
  };

  static ESTATE_ADVANCED_HEALTH_CARE_DIRECTIVE = {
    key: 'estate_advanced_health_care_directive',
    get value() {
      return Lang.t('document.advanced-health-care-directive');
    }
  };

  static ESTATE_ADVANCED_HEALTH_CARE_DIRECTIVE_SIGNED = {
    key: 'estate_advanced_health_care_directive_signed',
    get value() {
      return Lang.t('document.advanced-health-care-directive-signed');
    }
  };

  static ESTATE_SHIPPING_LABEL = {
    key: 'estate_shipping_label',
    get value() {
      return Lang.t('document.shipping-label');
    }
  };

  static ESTATE_AFFIDAVIT_OF_PUBLICATION = {
    key: 'estate_affidavit_of_publication',
    get value() {
      return Lang.t('document.affidavit-publication-of');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllAmericanJurisdictionsIds()]
  };

  static ESTATE_CERTIFIED_TRUE_MARRIAGE_CERTIFICATE = {
    key: 'estate_certified_true_marriage_certificate',
    get value() {
      return Lang.t('document.certified-true-certificate');
    }
  };

  static ESTATE_MARRIAGE_CONTRACT = {
    key: 'estate_marriage_contract',
    get value() {
      return Lang.t('document.marriage-contract');
    },
    metaType: DocumentMetaType.DOCUMENT_MARRIAGE_CONTRACT_META
  };

  static ESTATE_DOMESTIC_CONTRACT = {
    key: 'estate_domestic_contract',
    get value() {
      return Lang.t('Domestic contract');
    }
  };

  static ESTATE_SPOUSE_MARITAL_HISTORY_SUPPORTING_DOCUMENTATION = {
    key: 'estate_spouse_marital_history_supporting_documentation',
    get value() {
      return Lang.t('Spouse marital history supporting documentation');
    }
  };

  static ESTATE_CLOSING_LETTER = {
    key: 'estate_closing_letter',
    get value() {
      return Lang.t('document.closing-letter');
    },
    metaType: DocumentMetaType.DOCUMENT_CLOSING_LETTER_META,
    $isDisabled: true
  };

  static ESTATE_CODICILS = {
    key: 'estate_codicils',
    get value() {
      return Lang.t('document.codicils');
    }
  };

  static ESTATE_DIRECTION_AND_AUTHORIZATION_SIGNED = {
    key: 'estate_direction_and_authorization_signed',
    get value() {
      return Lang.t('document.direction-and-authorization-signed');
    }
  };

  static ESTATE_DIRECTION_AND_AUTHORIZATION = {
    key: 'estate_direction_and_authorization',
    get value() {
      return Lang.t('document.direction-and-authorization');
    }
  };

  static ESTATE_PROOF_OF_WILL_SEARCH_WITH_CHAMBRE_DES_NOTAIRES = {
    key: 'estate_proof_of_will_search_with_chambre_des_notaires',
    get value() {
      return Lang.t('document.proof-of-search-chambre');
    }
  };

  static ESTATE_PROOF_OF_WILL_SEARCH_WITH_BARREAU_DU_QUEBEC = {
    key: 'estate_proof_of_will_search_with_barreau_du_quebec',
    get value() {
      return Lang.t('document.proof-of-search-barreau');
    }
  };

  static ESTATE_COVER_LETTER_INVENTORY_AND_APPRAISAL = {
    key: 'estate_cover_letter_inventory_and_appraisal',
    get value() {
      return Lang.t('document.cover-inventory-appraisal');
    },
    jurisdictionIds: [PredefinedJurisdiction.CALIFORNIA.id]
  };

  static ESTATE_NOTICE_TO_CREDITORS = {
    key: 'estate_notice_to_creditors',
    get value() {
      return Lang.t('document.notice-to-creditors');
    }
  };

  static ESTATE_LETTER_TO_VICTIMS_COMPENSATION_BOARD = {
    key: 'estate_letter_to_victims_compensation_board',
    get value() {
      return Lang.t('document.letter-to-victims-compensation-board');
    }
  };

  static ESTATE_LETTER_TO_FRANCHISE_TAX_BOARD = {
    key: 'estate_letter_to_franchise_tax_board',
    get value() {
      return Lang.t('document.letter-to-franchise-tax-board');
    }
  };

  static ESTATE_LETTER_TO_DEPARTMENT_OF_HEALTH_CARE_SERVICES = {
    key: 'estate_letter_to_department_of_health_care_services',
    get value() {
      return Lang.t('document.letter-to-department-of-health-care-services');
    }
  };

  static ESTATE_FIDUCIARY_RELATIONSHIP_NOTICE = {
    key: 'estate_fiduciary_relationship_notice',
    get value() {
      return Lang.t('document.fiduciary-relationship-notice');
    }
  };

  static ESTATE_DEATH_CERTIFICATE = {
    key: 'estate_death_certificate',
    get value() {
      return Lang.t('document.death-certificate');
    }
  };

  static ESTATE_SPOUSE_DEATH_CERTIFICATE = {
    key: 'estate_spouse_death_certificate',
    get value() {
      return Lang.t('document.spouse-death-certificate');
    }
  };

  static ESTATE_DIVORCE_CERTIFICATE = {
    key: 'estate_divorce_certificate',
    get value() {
      return Lang.t('document.divorce-certificate');
    }
  };

  static ESTATE_DIRECTIONS_AND_AUTHORIZATION = {
    key: 'estate_directions_and_authorization',
    get value() {
      return Lang.t('document.directions-and-authorization');
    }
  };

  static ESTATE_EIN_CONFIRMATION_LETTER = {
    key: 'estate_ein_confirmation_letter',
    get value() {
      return Lang.t('document.ein-confirmation-letter');
    }
  };

  static ESTATE_EQUIFAX_OF_DEATH = {
    key: 'estate_equifax_of_death',
    get value() {
      return Lang.t('document.equifax-death-of');
    }
  };

  static ESTATE_EXPERIAN_OF_DEATH = {
    key: 'estate_experian_of_death',
    get value() {
      return Lang.t('document.experian-death-of');
    }
  };

  static ESTATE_EXISTING_WILL_OR_POWER_OF_ATTORNEY = {
    key: 'existing_will_or_power_of_attorney',
    get value() {
      return Lang.t('document.existing-will-attorney');
    }
  };

  static ESTATE_FUNERAL_DIRECTOR_STATEMENT_OF_DEATH = {
    key: 'estate_funeral_director_statement_of_death',
    get value() {
      return Lang.t('document.funeral-director-statement');
    }
  };

  static ESTATE_FORM_INSTRUCTIONS = {
    key: 'estate_form_instructions',
    get value() {
      return Lang.t('document.form-instructions');
    }
  };

  static ESTATE_GRANT_OF_PROBATE = {
    key: 'estate_grant_of_probate',
    get value() {
      return Lang.t('asset.grant-of-probate');
    }
  };

  static ESTATE_INFORMATION_RETURN = {
    key: 'estate_information_return',
    get value() {
      return Lang.t('document.estate-information-return');
    }
  };

  static ESTATE_INFORMATION_RETURN_SIGNED = {
    key: 'estate_information_return_signed',
    get value() {
      return Lang.t('document.estate-information-return');
    }
  };

  static ESTATE_NOTICE_OF_PETITION_TO_ADMINISTER_ESTATE = {
    key: 'estate_notice_of_petition_to_administer_estate',
    get value() {
      return Lang.t('document.notice-petition-administer');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllAmericanJurisdictionsIds()]
  };

  static ESTATE_ORDER_FOR_PROBATE = {
    key: 'estate_order_for_probate',
    get value() {
      return Lang.t('estatebanking.order-for-probate');
    }
  };

  static ESTATE_PETITION_FOR_PROBATE = {
    key: 'estate_petition_for_probate',
    get value() {
      return Lang.t('document.petition-for-probate');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllAmericanJurisdictionsIds()]
  };

  static ESTATE_PETITION_FOR_PROBATE_REJECTION = {
    key: 'estate_petition_for_probate_rejection',
    get value() {
      return Lang.t('document.probate-rejection');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllAmericanJurisdictionsIds()]
  };

  static ESTATE_GENERIC_PROBATE_EXTRA_DOCUMENT = {
    key: 'estate_generic_probate_extra_document',
    get value() {
      return Lang.t('document.probate-documents');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllCanadianJurisdictionsIds()]
  };

  static ESTATE_INVENTORY_AND_APPRAISAL_FORM = {
    key: 'estate_inventory_and_appraisal_form',
    get value() {
      return Lang.t('document.inventory-appraisal-form');
    },
    jurisdictionIds: [PredefinedJurisdiction.CALIFORNIA.id]
  };

  static ESTATE_PROBATE_COMMISSIONED_DOCUMENT = {
    key: 'estate_probate_commissioned_document',
    get value() {
      return Lang.t('document.probate-commissioned-document');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllCanadianJurisdictionsIds()]
  };

  static ESTATE_POWER_OF_ATTORNEY_HEALTHCARE_DIRECTIVE = {
    key: 'estate_power_of_attorney_healthcare_directive',
    get value() {
      return Lang.t('document.healthcare-directive');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_HEALTHCARE_DIRECTIVE_SIGNED = {
    key: 'estate_power_of_attorney_healthcare_directive_signed',
    get value() {
      return Lang.t('document.healthcare-directive-signed');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE = {
    key: 'estate_power_of_attorney_personal_care',
    get value() {
      return Lang.t('billing.power-for-care');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE_DOCX = {
    key: 'estate_power_of_attorney_personal_care_docx',
    get value() {
      return Lang.t('billing.power-for-care');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE_ESTATE_PROFESSIONAL_EDITED = {
    key: 'estate_power_of_attorney_personal_care_estate_professional_edited',
    get value() {
      return Lang.t('document.edited-power_of_attorney_personal_care');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE_PARTNER_EDITED = {
    key: 'estate_power_of_attorney_personal_care_partner_edited',
    get value() {
      return Lang.t('document.edited-power_of_attorney_personal_care');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE_FINAL_UPLOADED = {
    key: 'estate_power_of_attorney_personal_care_final_uploaded',
    get value() {
      return Lang.t('billing.power-for-care');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE_SIGNED = {
    key: 'estate_power_of_attorney_personal_care_signed',
    get value() {
      return Lang.t('document.power-attorney-for');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_PROPERTY = {
    key: 'estate_power_of_attorney_property',
    get value() {
      return Lang.t('document.power-of-attorney');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_PROPERTY_DOCX = {
    key: 'estate_power_of_attorney_property_docx',
    get value() {
      return Lang.t('document.power-of-attorney');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_PROPERTY_ESTATE_PROFESSIONAL_EDITED = {
    key: 'estate_power_of_attorney_property_estate_professional_edited',
    get value() {
      return Lang.t('document.edited-power-of-attorney-property');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_PROPERTY_PARTNER_EDITED = {
    key: 'estate_power_of_attorney_property_partner_edited',
    get value() {
      return Lang.t('document.edited-power-of-attorney-property');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_PROPERTY_FINAL_UPLOADED = {
    key: 'estate_power_of_attorney_property_final_uploaded',
    get value() {
      return Lang.t('document.power-of-attorney');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_PROPERTY_SIGNED = {
    key: 'estate_power_of_attorney_property_signed',
    get value() {
      return Lang.t('document.power-of-for');
    }
  };

  static ESTATE_POWER_OF_ATTORNEY_PROPERTY_AND_PERSONAL_CARE = {
    key: 'estate_power_of_attorney_property_and_personal_care',
    get value() {
      return Lang.t('document.power-and-personal');
    },
    jurisdictionIds: [PredefinedJurisdiction.SASKATCHEWAN.id]
  };

  static ESTATE_POWER_OF_ATTORNEY_PROPERTY_AND_PERSONAL_CARE_DOCX = {
    key: 'estate_power_of_attorney_property_and_personal_care_docx',
    get value() {
      return Lang.t('document.power-and-personal');
    },
    jurisdictionIds: [PredefinedJurisdiction.SASKATCHEWAN.id]
  };

  static ESTATE_POWER_OF_ATTORNEY_PROPERTY_AND_PERSONAL_CARE_ESTATE_PROFESSIONAL_EDITED = {
    key: 'estate_power_of_attorney_property_and_personal_care_estate_professional_edited',
    get value() {
      return Lang.t('document.edited-power-and-personal');
    },
    jurisdictionIds: [PredefinedJurisdiction.SASKATCHEWAN.id]
  };

  static ESTATE_POWER_OF_ATTORNEY_PROPERTY_AND_PERSONAL_CARE_PARTNER_EDITED = {
    key: 'estate_power_of_attorney_property_and_personal_care_partner_edited',
    get value() {
      return Lang.t('document.edited-power-and-personal');
    },
    jurisdictionIds: [PredefinedJurisdiction.SASKATCHEWAN.id]
  };

  static ESTATE_POWER_OF_ATTORNEY_PROPERTY_AND_PERSONAL_CARE_FINAL_UPLOADED = {
    key: 'estate_power_of_attorney_property_and_personal_care_final_uploaded',
    get value() {
      return Lang.t('document.power-and-personal');
    },
    jurisdictionIds: [PredefinedJurisdiction.SASKATCHEWAN.id]
  };

  static ESTATE_POWER_OF_ATTORNEY_PROPERTY_AND_PERSONAL_CARE_SIGNED = {
    key: 'estate_power_of_attorney_property_and_personal_care_signed',
    get value() {
      return Lang.t('document.power-for-care');
    },
    jurisdictionIds: [PredefinedJurisdiction.SASKATCHEWAN.id]
  };

  static ESTATE_PROBATE_APPLICATION_COVER_LETTER = {
    key: 'estate_probate_application_cover_letter',
    get value() {
      return Lang.t('document.cover-letter');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllCanadianJurisdictionsIds()]
  };

  static ESTATE_PROBATE_APPLICATION_INSTRUCTIONS = {
    key: 'estate_probate_application_instructions',
    get value() {
      return Lang.t('document.instructions-how-to');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllCanadianJurisdictionsIds()]
  };

  static ESTATE_PROBATE_DE_172 = {
    key: 'estate_probate_de_172',
    get value() {
      return Lang.t('document.probate-de');
    },
    jurisdictionIds: [PredefinedJurisdiction.CALIFORNIA.id]
  };

  static ESTATE_PROBATE_DE_174 = {
    key: 'estate_probate_de_174',
    get value() {
      return Lang.t('document.9d73960f-e95e-4424-a0d4-04dca28ed6ea');
    },
    jurisdictionIds: [PredefinedJurisdiction.CALIFORNIA.id]
  };

  static ESTATE_PROBATE_FILING_INSTRUCTIONS = {
    key: 'estate_probate_filing_instructions',
    get value() {
      return Lang.t('document.filing-instructions');
    }
  };

  static ESTATE_PROBATE_INFORMATION_FORM = {
    key: 'estate_probate_information_form',
    get value() {
      return Lang.t('document.information-form');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllCanadianJurisdictionsIds()]
  };

  static ESTATE_PROBATE_FINAL_JUDGEMENT = {
    key: 'estate_probate_final_judgement',
    get value() {
      return Lang.t('document.final-judgement');
    },
    jurisdictionIds: [PredefinedJurisdiction.QUEBEC.id]
  };

  static ESTATE_PROBATE_SIGNATURE_INSTRUCTIONS = {
    key: 'estate_probate_signature_instructions',
    get value() {
      return Lang.t('document.signature-instructions');
    }
  };

  static ESTATE_PROTECTION_MANDATE = {
    key: 'estate_protection_mandate',
    get value() {
      return Lang.t('document.protection-mandate');
    },
    jurisdictionIds: [PredefinedJurisdiction.QUEBEC.id]
  };

  static ESTATE_PROTECTION_MANDATE_DOCX = {
    key: 'estate_protection_mandate_docx',
    get value() {
      return Lang.t('document.protection-mandate');
    },
    jurisdictionIds: [PredefinedJurisdiction.QUEBEC.id]
  };

  static ESTATE_PROTECTION_MANDATE_ESTATE_PROFESSIONAL_EDITED = {
    key: 'estate_protection_mandate_estate_professional_edited',
    get value() {
      return Lang.t('document.edited-protection-mandate');
    },
    jurisdictionIds: [PredefinedJurisdiction.QUEBEC.id]
  };

  static ESTATE_PROTECTION_MANDATE_PARTNER_EDITED = {
    key: 'estate_protection_mandate_partner_edited',
    get value() {
      return Lang.t('document.edited-protection-mandate');
    },
    jurisdictionIds: [PredefinedJurisdiction.QUEBEC.id]
  };

  static ESTATE_PROTECTION_MANDATE_FINAL_UPLOADED = {
    key: 'estate_protection_mandate_final_uploaded',
    get value() {
      return Lang.t('document.protection-mandate');
    },
    jurisdictionIds: [PredefinedJurisdiction.QUEBEC.id]
  };

  static ESTATE_PROTECTION_MANDATE_TRUE_CERTIFIED_COPY = {
    key: 'estate_protection_mandate_true_certified_copy',
    get value() {
      return Lang.t('document.true-of-protection');
    },
    jurisdictionIds: [PredefinedJurisdiction.QUEBEC.id]
  };

  static ESTATE_PROTECTION_MANDATE_SIGNED = {
    key: 'estate_protection_mandate_signed',
    get value() {
      return Lang.t('document.protection-mandate-signed');
    },
    jurisdictionIds: [PredefinedJurisdiction.QUEBEC.id]
  };

  static ESTATE_QUEBEC_DESIGNATION_OF_LIQUIDATOR = {
    key: 'estate_quebec_designation_of_liquidator',
    get value() {
      return Lang.t('document.designation-of-liquidator');
    },
    jurisdictionIds: [PredefinedJurisdiction.QUEBEC.id]
  };

  static ESTATE_QUEBEC_INFORMATION_ABOUT_REPRESENTATIVE = {
    key: 'estate_quebec_information_about_representative',
    get value() {
      return Lang.t('document.information-about-a-representative');
    },
    jurisdictionIds: [PredefinedJurisdiction.QUEBEC.id]
  };

  static ESTATE_QUEBEC_CLOSURE_OF_INVENTORY_RDPRM = {
    key: 'estate_quebec_closure_of_inventory_rdprm',
    get value() {
      return Lang.t('document.closure-of-inventory');
    },
    jurisdictionIds: [PredefinedJurisdiction.QUEBEC.id]
  };

  static ESTATE_SCANNED_MARRIAGE_CERTIFICATE = {
    key: 'estate_scanned_marriage_certificate',
    get value() {
      return Lang.t('document.scanned-marriage-certificate');
    }
  };

  static ESTATE_STATUTORY_DECLARATION_OF_COMMON_LAW_UNION = {
    key: 'estate_statutory_declaration_of_common_law_union',
    get value() {
      return Lang.t('document.statutory-declaration-union');
    }
  };

  static ESTATE_TRANSUNION_OF_DEATH = {
    key: 'estate_transunion_of_death',
    get value() {
      return Lang.t('document.transunion-of-death');
    }
  };

  static ESTATE_WILL = {
    key: 'estate_will',
    get value() {
      return Lang.t('document.will');
    },
    metaType: DocumentMetaType.DOCUMENT_WILL_META
  };

  static ESTATE_WILL_GUIDE_INSTRUCTIONS = {
    key: 'estate_will_guide_instructions',
    get value() {
      return Lang.t('document.will-guide-instructions');
    }
  };

  static ESTATE_WILL_COVER_PAGE = {
    key: 'estate_will_cover_page',
    get value() {
      return Lang.t('document.will-cover-page');
    }
  };

  static ESTATE_WILL_COVERSHEET = {
    key: 'estate_will_coversheet',
    get value() {
      return Lang.t('document.will-coversheet');
    }
  };

  static ESTATE_WILL_DOCX = {
    key: 'estate_will_docx',
    get value() {
      return Lang.t('document.will');
    },
    metaType: DocumentMetaType.DOCUMENT_WILL_META
  };

  static ESTATE_WILL_ESTATE_PROFESSIONAL_EDITED_DOCX = {
    key: 'estate_will_estate_professional_edited_docx',
    get value() {
      return Lang.t('document.edited-will');
    }
  };

  static ESTATE_WILL_EDITED = {
    key: 'estate_will_edited',
    get value() {
      return Lang.t('document.edited-will');
    }
  };

  static ESTATE_WILL_FINAL_UPLOADED = {
    key: 'estate_will_final_uploaded',
    get value() {
      return Lang.t('document.will-uploaded-final');
    }
  };

  static ESTATE_WILL_FINAL = {
    key: 'estate_will_final',
    get value() {
      return Lang.t('document.will-final');
    }
  };

  static ESTATE_WILLS_NOTICE_SEARCH_RESULTS_APPLICATION = {
    key: 'estate_wills_notice_search_application',
    get value() {
      return Lang.t('document.wills-notice-search');
    }
  };

  static ESTATE_WILLS_NOTICE_SEARCH_RESULTS = {
    key: 'estate_wills_notice_search_results',
    get value() {
      return Lang.t('document.wills-notice-search-results');
    }
  };

  static ESTATE_WILL_TRUE_CERTIFIED_COPY = {
    key: 'estate_will_true_certified_copy',
    get value() {
      return Lang.t('document.true-copy-will');
    }
  };

  static ESTATE_WILL_SIGNED = {
    key: 'estate_will_signed',
    get value() {
      return Lang.t('document.will-signed');
    },
    metaType: DocumentMetaType.DOCUMENT_WILL_META
  };

  static ESTATE_LETTER_OF_FINAL_WISHES = {
    key: 'estate_letter_of_final_wishes',
    get value() {
      return Lang.t('document.letter-of-final-wishes');
    }
  };

  static ESTATE_PROBATE_REFEREE_STATEMENT_OF_BOND = {
    key: 'estate_probate_referee_statement_of_bond',
    get value() {
      return Lang.t('document.probate-statement-bond');
    }
  };

  static ESTATE_PROBATE_APPLICATION_FORM = {
    key: 'estate_probate_application_form',
    get value() {
      return Lang.t('document.probate-application-form');
    }
  };

  static ESTATE_SIGNED_PROBATE_APPLICATION_FORM = {
    key: 'estate_signed_probate_application_form',
    get value() {
      return Lang.t('probate.signed-application-form');
    }
  };

  static ESTATE_CLAIMS_AND_NOTICES = {
    key: 'estate_claims_and_notices',
    get value() {
      return Lang.t('document.claims-notices');
    }
  };

  static TAX_CANADA_PENSION_PLAN = {
    key: 'tax_canada_pension_plan',
    get value() {
      return Lang.t('document.plan-death-benefits');
    }
  };

  static TAX_CPP_SURVIVORS_PENSION = {
    key: 'tax_cpp_survivors_pension',
    get value() {
      return Lang.t('document.plan-survivor-pension');
    }
  };

  static TAX_NOTICE_OF_ASSESSMENT = {
    key: 'tax_notice_of_assessment',
    get value() {
      return Lang.t('document.notice-of-assessment');
    }
  };

  static TAX_OWING_VOUCHER = {
    key: 'tax_owing_voucher',
    get value() {
      return Lang.t('document.owing-voucher');
    }
  };

  static TAX_OTHERS = {
    key: 'tax_others',
    get value() {
      return Lang.t('asset.other');
    },
    metaType: DocumentMetaType.DOCUMENT_OTHER_META
  };

  static TAX_REPORT = {
    key: 'tax_report',
    get value() {
      return Lang.t('document.tax-report');
    }
  };

  static TAX_UPDATE_CRA_RECORDS = {
    key: 'tax_update_cra_records',
    get value() {
      return Lang.t('document.update-cra-records');
    }
  };

  static TAX_DONATION_RECEIPT = {
    key: 'tax_donation_receipt',
    get value() {
      return Lang.t('document.donation-receipt');
    }
  };

  static TAX_TAXABLE_EXPENSES = {
    key: 'tax_taxable_expenses',
    get value() {
      return Lang.t('document.taxable-expenses');
    }
  };

  static TAX_T1 = {
    key: 'tax_t1',
    get value() {
      return Lang.t('document.t1-tax-return');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllCanadianJurisdictionsIds()]
  };

  static TAX_T3 = {
    key: 'tax_t3',
    get value() {
      return Lang.t('document.income-tax-return');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllCanadianJurisdictionsIds()]
  };

  static TAX_T183 = {
    key: 'tax_t183',
    get value() {
      return Lang.t('document.t183-information-form');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllCanadianJurisdictionsIds()]
  };

  static TAX_FORM_1040 = {
    key: 'tax_form_1040',
    get value() {
      return Lang.t('document.form-individual-income');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllAmericanJurisdictionsIds()]
  };

  static TAX_FORM_1041 = {
    key: 'tax_form_1041',
    get value() {
      return Lang.t('document.form-estates-trusts');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllAmericanJurisdictionsIds()]
  };

  static TAX_RTR = {
    key: 'tax_rtr',
    get value() {
      return Lang.t('document.rights-things-return');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllCanadianJurisdictionsIds()]
  };

  static TAX_CRA_CC = {
    key: 'tax_cra_cc',
    get value() {
      return Lang.t('document.cra-clearance-certificate');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllCanadianJurisdictionsIds()]
  };

  static TAX_T2125_BUSINESS_SCHEDULE = {
    key: 'tax_t2125_business_schedule',
    get value() {
      return Lang.t('document.t2125-business-schedule');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllCanadianJurisdictionsIds()]
  };

  static TAX_T776_STATEMENT_OF_REAL_ESTATE_RENTALS = {
    key: 'tax_t776_statement_of_real_estate_rentals',
    get value() {
      return Lang.t('document.statement-estate-rentals');
    },
    jurisdictionIds: [...PredefinedJurisdiction.getAllCanadianJurisdictionsIds()]
  };

  static OTHER_DIVORCE_CERTIFICATE = {
    key: 'other_divorce_certificate',
    get value() {
      return Lang.t('document.divorce-certificate');
    },
    jurisdictionIds: [PredefinedJurisdiction.QUEBEC.id]
  };

  static COHABITATION_AGREEMENT = {
    key: 'cohabitation_agreement',
    get value() {
      return Lang.t('Cohabitation agreement');
    }
  };

  static ESTATE_QUEBEC_DESIGNATION_OF_LIQUIDATOR_BY_HEIRS = {
    key: 'estate_quebec_designation_of_liquidator_by_heirs',
    get value() {
      return Lang.t('document.designation-of-liquidator-by-heirs');
    },
    jurisdictionIds: [PredefinedJurisdiction.QUEBEC.id]
  };

  static ESTATE_OTHERS = {
    key: 'estate_others',
    get value() {
      return Lang.t('document.other');
    },
    metaType: DocumentMetaType.DOCUMENT_OTHER_META
  };

  static getDocumentSubTypeFromAssetType(asset) {
    switch (asset.type) {
      case AssetType.ART_JEWELRY.key:
      case AssetType.BELONGING.key:
      case AssetType.VEHICLE.key:
        return [DocumentSubType.ASSET_APPRAISAL, DocumentSubType.ASSET_OTHERS];
      case AssetType.BANK_ACCOUNT.key:
        return [DocumentSubType.ASSET_BANK_STATEMENTS, DocumentSubType.ASSET_OTHERS];
      case AssetType.CRYPTOCURRENCY.key:
        return [DocumentSubType.ASSET_TAX_REPORT, DocumentSubType.ASSET_OTHERS];
      case AssetType.FIREARM.key:
        return [DocumentSubType.ASSET_FIREARM_LICENSE, DocumentSubType.ASSET_OTHERS];
      case AssetType.LIFE_INSURANCE.key:
      case AssetType.PENSION_PLAN.key:
        return [DocumentSubType.ASSET_POLICY_CONTRACT, DocumentSubType.ASSET_OTHERS];
      case AssetType.PRIVATE_STOCK.key:
        return [DocumentSubType.ASSET_STOCK_OPTIONS, DocumentSubType.ASSET_OTHERS];
      case AssetType.REAL_ESTATE.key:
        return [DocumentSubType.ASSET_PROPERTY_TITLE, DocumentSubType.ASSET_OTHERS];
      case AssetType.CASH.key:
      case AssetType.FUNERAL_PRE_ARRANGEMENT.key:
      default:
        return [DocumentSubType.ASSET_OTHERS];
    }
  }

  static getDocumentSubTypeFromLiability(liability) {
    switch (liability.type) {
      case LiabilityType.LOAN_AND_CREDIT_PAYMENT.key:
        return [DocumentSubType.LIABILITY_BANK_STATEMENTS, DocumentSubType.LIABILITY_OTHERS];
      case LiabilityType.UNPAID_TAX.key:
        return [DocumentSubType.LIABILITY_TAX, DocumentSubType.LIABILITY_OTHERS];
      case LiabilityType.PERSONAL_EXPENSE.key:
        if (DocumentSubType.hasPersonalExpenseLiabilityBill(liability)) {
          return [DocumentSubType.LIABILITY_BILL, DocumentSubType.LIABILITY_OTHERS];
        }
        return [DocumentSubType.LIABILITY_LEASE, DocumentSubType.LIABILITY_OTHERS];
      case LiabilityType.ESTATE_EXPENSE.key:
        return DocumentSubType.getEstateExpenseDocumentSubTypes(liability);
      case LiabilityType.EXECUTOR_DISBURSEMENT.key:
        return [
          DocumentSubType.LIABILITY_EXECUTOR_DISBURSEMENT_INVOICE,
          DocumentSubType.LIABILITY_OTHERS
        ];
      case LiabilityType.OTHER_CREDITOR.key:
      case LiabilityType.EXECUTOR_COMPENSATION.key:
      default:
        return [DocumentSubType.LIABILITY_OTHERS];
    }
  }

  static hasPersonalExpenseLiabilityBill(liability) {
    return ![PersonalExpenseType.RENT.key, PersonalExpenseType.CAR_LEASE.key].includes(
      liability.meta.type
    );
  }

  static hasEstateExpenseLiabilityBill(liability) {
    return [
      FuneralAndEstateExpenseType.OTHER.key,
      FuneralAndEstateExpenseType.LEGAL_FEES.key,
      FuneralAndEstateExpenseType.ADMINISTRATION_FEES.key,
      FuneralAndEstateExpenseType.ACCOUNTING_FEES.key
    ].includes(liability.meta.type);
  }

  static getEstateExpenseDocumentSubTypes(liability) {
    if (liability.meta.type === FuneralAndEstateExpenseType.APPRAISAL_FEES.key) {
      return [
        DocumentSubType.LIABILITY_APPRAISAL,
        DocumentSubType.LIABILITY_FUNERAL_CONTRACT,
        DocumentSubType.LIABILITY_FUNERAL_INVOICE,
        DocumentSubType.LIABILITY_OTHERS
      ];
    }
    if (DocumentSubType.hasEstateExpenseLiabilityBill(liability)) {
      return [
        DocumentSubType.LIABILITY_BILL,
        DocumentSubType.LIABILITY_FUNERAL_CONTRACT,
        DocumentSubType.LIABILITY_FUNERAL_INVOICE,
        DocumentSubType.LIABILITY_OTHERS
      ];
    }
    return [
      DocumentSubType.LIABILITY_FUNERAL_INVOICE,
      DocumentSubType.LIABILITY_FUNERAL_CONTRACT,
      DocumentSubType.LIABILITY_OTHERS
    ];
  }

  static getAllCanadianEstateTaxForms() {
    return [
      DocumentSubType.TAX_T1,
      DocumentSubType.TAX_T183.key,
      DocumentSubType.TAX_T3,
      DocumentSubType.TAX_RTR,
      DocumentSubType.TAX_CRA_CC,
      DocumentSubType.TAX_NOTICE_OF_ASSESSMENT,
      DocumentSubType.TAX_T2125_BUSINESS_SCHEDULE,
      DocumentSubType.TAX_T776_STATEMENT_OF_REAL_ESTATE_RENTALS,
      DocumentSubType.TAX_OTHERS
    ];
  }

  /**
   * @param type
   * @returns {boolean}
   */
  static canEsignTaxFormType(type) {
    return [
      DocumentSubType.TAX_T1.key,
      DocumentSubType.TAX_T183.key,
      DocumentSubType.TAX_T3.key
    ].includes(type);
  }

  static getAllAmericanEstateTaxForm() {
    return [
      DocumentSubType.TAX_FORM_1040,
      DocumentSubType.TAX_FORM_1041,
      DocumentSubType.TAX_OTHERS
    ];
  }

  static getJurisdictionEstateTaxForms(jurisdictionId) {
    if (PredefinedJurisdiction.isCanadianJurisdiction(jurisdictionId)) {
      return DocumentSubType.getAllCanadianEstateTaxForms();
    }
    if (PredefinedJurisdiction.isAmericanJurisdiction(jurisdictionId)) {
      return DocumentSubType.getAllAmericanEstateTaxForm();
    }
  }

  static getValuesForDocumentType(documentTypeKey) {
    return DocumentSubType.getValues().filter(
      (subType) => DocumentType.findTypeFromSubTypeKey(subType.key).key === documentTypeKey
    );
  }

  static getJurisdictionSubTypesForDocumentType(documentTypeKey) {
    const subTypes = super.getValuesFromParentTypeKey(documentTypeKey);
    const jurisdictionId = getters.auth.getCurrentEstate()?.jurisdictionId;
    return subTypes.filter(
      (subType) =>
        !subType.jurisdictionIds?.length || subType.jurisdictionIds.includes(jurisdictionId)
    );
  }

  /**
   * @param type
   * @returns {boolean}
   */
  static isIncomeTaxAndBenefitReturnTaxFormTypeTax(type) {
    return [DocumentSubType.TAX_T1.key, DocumentSubType.TAX_T183.key].includes(type);
  }

  static isEditedSubType(type) {
    return [
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY_AND_PERSONAL_CARE_PARTNER_EDITED.key,
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY_PARTNER_EDITED.key,
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE_PARTNER_EDITED.key,
      DocumentSubType.ESTATE_PROTECTION_MANDATE_PARTNER_EDITED.key,
      DocumentSubType.ESTATE_WILL_EDITED.key
    ].includes(type);
  }

  static isTrueCertifiedCopySubType(type) {
    return [
      DocumentSubType.ESTATE_PROTECTION_MANDATE_TRUE_CERTIFIED_COPY.key,
      DocumentSubType.ESTATE_WILL_TRUE_CERTIFIED_COPY.key
    ].includes(type);
  }

  static isClaraAvailable(type) {
    return [
      DocumentSubType.ESTATE_WILL.key,
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE.key,
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY.key
    ].includes(type);
  }
}
